// src/components/sponsor/events/SponsorEventsPage.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchSponsorableEvents } from "@/store/slices/sponsorSlice";
import { Calendar, MapPin, Package, Search, Users } from "lucide-react";
import Button from "@/components/common/Button";
import SelectDropdown from "@/components/common/SelectDropdown";
import {
  EVENT_TYPES as RAW_EVENT_TYPES,
  SPONSORSHIP_COMPONENTS,
  getComponentLabel,
} from "@/utils/constants";
import EventFilters from "@/components/common/EventFilters";
import useFilterParams from "@/hooks/useFilterParams";

const EVENT_TYPES = [{ value: "", label: "All Types" }, ...RAW_EVENT_TYPES];

const DEFAULT_FILTERS = {
  search: "",
  event_type: "",
  component: "",
  city: "",
  startDate: "",
  endDate: "",
  sort: "recent"
};

const SponsorEventsPage = () => {
  const dispatch = useDispatch();
  const { events, isLoading, sponsorComponentCounts, eventsPagination } = useSelector((state) => state.sponsor);
  
  const [filters, handleFilterChange] = useFilterParams(DEFAULT_FILTERS);

  const [page, setPage] = useState(1);
  const [limit] = useState(12);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    const params = {
      page,
      limit,
      ...filters
    };
    
    // Remove empty strings
    Object.keys(params).forEach(key => {
      if (params[key] === "") {
        delete params[key];
      }
    });

    dispatch(fetchSponsorableEvents(params));
  }, [dispatch, filters, page, limit]);

  const getEventTypeLabel = (type) => {
    const found = RAW_EVENT_TYPES.find((t) => t.value === type);
    return found ? found.label : type;
  };

  const hasFilters = Object.entries(filters).some(([key, value]) => key !== 'sort' && value !== '');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Browse Events
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Find events open for sponsorship
        </p>
      </div>

      <EventFilters
        filters={filters}
        onChange={handleFilterChange}
        componentCounts={sponsorComponentCounts}
        showStatus={false}
      />

      {/* Events Grid */}
      {isLoading ? (
        <div className="py-12 text-center text-gray-500">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="py-12 text-center">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {hasFilters ? "No events match your filters." : "No events available for sponsorship."}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event, index) => (
              <motion.div
              key={event._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Event Card Header */}
              <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20">
                <span className="inline-block px-2 py-1 text-xs font-medium text-teal-700 dark:text-teal-300 bg-teal-100 dark:bg-teal-900/30 rounded-full mb-2">
                  {getEventTypeLabel(event.event_type)}
                </span>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 line-clamp-2">
                  {event.title}
                </h3>
              </div>

              {/* Event Card Body */}
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {new Date(event.start_date).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
                
                {event.location?.city && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    {event.location.city}
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Users className="w-4 h-4 text-gray-400" />
                  {event.capacity === 0 ? "Unlimited" : `${event.capacity} participants`}
                </div>

                <div className="flex items-center gap-2 text-sm font-medium text-teal-600 dark:text-teal-400">
                  <Package className="w-4 h-4" />
                  {event.sponsorship_components?.length || 0} sponsorship components
                </div>

                {/* Component category badges */}
                {event.sponsorship_components?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {event.sponsorship_components.map((comp) => (
                      <span
                        key={comp.id || comp._id}
                        className="text-xs px-2 py-0.5 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800 rounded-full"
                      >
                        {getComponentLabel(comp.category)}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Event Card Footer */}
              <div className="p-4 border-t border-gray-100 dark:border-gray-700">
                <Link to={`/sponsor/events/${event._id}`}>
                  <Button 
                    variant="outline" 
                    className="w-full border-teal-200 dark:border-teal-800 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20"
                  >
                    View Details
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
          </div>

          {eventsPagination && eventsPagination.pages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-lg mt-6 shadow-sm">
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing{" "}
                    <span className="font-medium">
                      {(eventsPagination.page - 1) * limit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(eventsPagination.page * limit, eventsPagination.total)}
                    </span>{" "}
                    of <span className="font-medium">{eventsPagination.total}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:ring-gray-600 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    >
                      <span className="sr-only">Previous</span>
                      &larr;
                    </button>
                    {/* Simplified page numbers for UI */}
                    <button className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600">
                      Page {page} of {eventsPagination.pages}
                    </button>
                    <button
                      onClick={() => setPage(Math.min(eventsPagination.pages, page + 1))}
                      disabled={page === eventsPagination.pages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:ring-gray-600 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    >
                      <span className="sr-only">Next</span>
                      &rarr;
                    </button>
                  </nav>
                </div>
              </div>
              
              {/* Mobile pagination */}
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Previous
                </button>
                <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  {page} / {eventsPagination.pages}
                </div>
                <button
                  onClick={() => setPage(Math.min(eventsPagination.pages, page + 1))}
                  disabled={page === eventsPagination.pages}
                  className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SponsorEventsPage;
